import type { MarketCountryCode } from '@backmarket/http-api'

import type { ListingV2, MarketListing } from '~/api/listings/types'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { shouldDisplaySalesMaximizerButton } from '../components/ListingsTable/components/ExpandedListing/components/PricingStrategyCell/pricingStrategy.helpers'

export function useListingsTracking() {
  const { isFeatureEnabled } = useBusinessFeaturesStore()
  const isSalesMaximizerPriceEnabled = isFeatureEnabled(
    'isSalesMaximizerPriceEnabled',
  )

  function formatListingToTrackingEvent(
    listing: ListingV2,
    forMarket:
      | (MarketListing & { countryCode: MarketCountryCode })
      | null = null,
  ) {
    const onlineMarkets = [
      ...Object.keys(listing.markets.withBackbox).map((market) => ({
        countryCode: market,
        ...listing.markets.withBackbox[market as MarketCountryCode],
      })),
      ...Object.keys(listing.markets.withoutBackbox).map((market) => ({
        countryCode: market,
        ...listing.markets.withoutBackbox[market as MarketCountryCode],
      })),
    ] as (MarketListing & { countryCode: MarketCountryCode })[]

    const markets = forMarket === null ? onlineMarkets : [forMarket]

    return {
      listing_id: listing.id,
      listing_title: listing.title,
      product_public_id: listing.productId,
      listing_backbox_grade: listing.aestheticGrade.value,
      listing_special_offer_type: listing.specialOfferType.value,
      listing_markets: markets.map((listingMarket) => {
        const isOptimalPriceDisplayed =
          isSalesMaximizerPriceEnabled &&
          shouldDisplaySalesMaximizerButton(listingMarket)

        return {
          market: listingMarket.countryCode,
          currency: listingMarket.price?.currency ?? '',
          has_backbox: listingMarket.hasBackbox,
          is_optimal_price_displayed: isOptimalPriceDisplayed,
          minimum_price: listingMarket.minimumPrice?.amount ?? '',
          target_price: listingMarket.price?.amount ?? '',
          ...(listingMarket.hasBackbox && {
            selling_price: listingMarket.backboxPrice?.amount ?? '',
            backbox_grade: listingMarket.backboxGrade.value,
          }),
          ...(!listingMarket.hasBackbox && {
            selling_price: listingMarket.currentBackboxPrice?.amount ?? '',
            price_to_win_backbox: listingMarket.backboxPrice?.amount ?? '',
          }),
          ...(isOptimalPriceDisplayed && {
            optimal_price:
              listingMarket.pricingStrategy?.salesMaximizerPrice?.amount ?? '',
            range_estimaded_sales:
              listingMarket.pricingStrategy?.estimatedDemand ?? '',
          }),
        }
      }),
    }
  }

  return {
    formatListingToTrackingEvent,
  }
}
